import { Fragment } from "react";
import { Sidebar } from "primereact/sidebar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { ActionTag } from "./AuditLogTags";
import { auditSentence, browserTimeZone, timeZoneName } from "../../../helpers/AuditLogHelper";
import { AUDIT_HIDDEN } from "../../../constants/AuditLogConstants";
import "./audit-logs.scss";

// "09 Oct 2026, 10:31:07 am" in the browser's time zone.
const formatWhen = (date) => {
  const p = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: browserTimeZone(),
    hourCycle: "h12",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(date)
    .forEach((part) => {
      p[part.type] = part.value;
    });
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}:${p.second} ${(p.dayPeriod || "").toLowerCase()}`;
};

// An entry's title: "Role: HR Admin". An explicit entry has no record type, so
// its title is its action and the record name: "Sign-in failed: With password".
const entryTitle = (details) => {
  if (details.recordType && details.recordName) return `${details.recordType}: ${details.recordName}`;
  if (details.recordName) return `${details.action}: ${details.recordName}`;
  return details.action;
};

// The details of one audit entry, in a panel on the right of the list.
const AuditLogDetails = ({ visible, details, requesting, onHide }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  // "(hidden)" is italic as well as grey, so colour is not its only sign.
  const shown = (value) =>
    value === AUDIT_HIDDEN ? <span className="audit-hidden">{value}</span> : value ?? "-";

  // In a permission row, the access words After has and Before did not are bold and green.
  const shownAfter = (change) => {
    if (details.fieldHeader !== "Permission" || change.after === AUDIT_HIDDEN || !change.after) {
      return shown(change.after);
    }
    const had = new Set((change.before || "").split(", "));
    return change.after.split(", ").map((word, index) => (
      <Fragment key={word}>
        {index > 0 && ", "}
        {had.has(word) ? word : <strong className="audit-added">{word}</strong>}
      </Fragment>
    ));
  };

  const renderEntry = () => {
    const when = new Date(details.createdAt);
    const who = details.actor?.isSystem
      ? [details.actor?.source]
      : [details.actor?.email, details.actor?.role];

    return (
      <>
        <header className="audit-details-header">
          <ActionTag action={details.action} />
          <h2>{entryTitle(details)}</h2>
          <p>{auditSentence(details, intl.formatMessage)}</p>
        </header>
        <dl className="audit-details-facts">
          <dt>{t("audit_details_when")}</dt>
          <dd>
            <span>{formatWhen(when)}</span>{" "}
            <span className="audit-muted-inline">({timeZoneName(when)})</span>
          </dd>
          <dt>{t("audit_details_who")}</dt>
          <dd>
            {details.actor?.name}{" "}
            <span className="audit-muted-inline">{who.filter(Boolean).join(", ")}</span>
          </dd>
          <dt>{t("audit_details_area")}</dt>
          <dd>{details.area}</dd>
          <dt>{t("audit_details_record")}</dt>
          <dd>
            {details.recordName || "-"}{" "}
            {details.recordLink && (
              <Link to={details.recordLink} className="audit-open-link">
                {t("audit_open_record")} <i className="pi pi-external-link" aria-hidden="true" />
              </Link>
            )}
            {details.recordDetail && <span className="audit-muted">{details.recordDetail}</span>}
          </dd>
        </dl>
        {/* An entry without field values (an SMTP change, a sign-in) has only its sentence. */}
        {details.changes?.length > 0 && (
          <>
            <h3>{t("audit_what_changed")}</h3>
            <table className="audit-diff">
              <thead>
                <tr>
                  <th scope="col">{t(details.fieldHeader === "Permission" ? "audit_permission" : "audit_field")}</th>
                  <th scope="col">{t("audit_before")}</th>
                  <th scope="col">{t("audit_after")}</th>
                </tr>
              </thead>
              <tbody>
                {details.changes.map((change, index) => (
                  <tr key={`${change.field}-${index}`}>
                    <th scope="row">{change.field}</th>
                    <td className="audit-before">{shown(change.before)}</td>
                    <td>{shownAfter(change)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <p className="audit-note">
          <i className="pi pi-lock" aria-hidden="true" />
          <span>{t("audit_secrets_note")}</span>
        </p>
      </>
    );
  };

  const ready = !requesting && details;
  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      className="audit-details"
      aria-label={ready ? entryTitle(details) : t("audit_entry")}
      ariaCloseLabel={t("audit_details_close")}
    >
      {ready ? renderEntry() : <ProgressSpinner aria-label={t("audit_loading_entry")} />}
    </Sidebar>
  );
};

export default AuditLogDetails;
